import { useState } from "react";
import { Link } from "react-router-dom";
import PageHeader from "@/components/base/PageHeader";
import StatCard from "@/components/base/StatCard";
import Card, { SectionTitle } from "@/components/base/Card";
import Badge from "@/components/base/Badge";
import Button from "@/components/base/Button";
import { LoadingState } from "@/components/base/StateViews";
import DateRangeBar from "@/components/feature/DateRangeBar";
import { useAsync } from "@/hooks/useAsync";
import { useAuth } from "@/contexts/AuthContext";
import { getClasses } from "@/services/academicService";
import { getEnrollments } from "@/services/studentService";
import { getTeachers, getStaff } from "@/services/hrService";
import { getCycles, getActiveSchoolYear, getActivities } from "@/services/schoolService";
import { getByCycle, getOverview } from "@/services/reportService";
import { formatMoney } from "@/utils/format";
import { defaultPeriod, type Period } from "@/utils/period";
import ActivityFeed from "./components/ActivityFeed";
import ClassDistribution from "./components/ClassDistribution";
import FinanceOverview from "./components/FinanceOverview";
import { cycleIdOf } from "@/utils/cycleScope";
import { isInClass } from "@/utils/enrollment";

/** Une donnée réservée à une permission : vide si le rôle n'y a pas accès (le tableau de bord reste utilisable). */
const ifAllowed = <T,>(allowed: boolean, load: () => Promise<T>, empty: T): Promise<T> =>
  allowed ? load().catch(() => empty) : Promise.resolve(empty);

export default function DashboardHome() {
  const schoolYear = getActiveSchoolYear();
  const { can } = useAuth();
  const [period, setPeriod] = useState<Period>(defaultPeriod);
  const canReports = can("reports:read");

  const { data, loading, error, reload } = useAsync(
    async () => {
      const [cycles, classes, enrollments, teachers, staff, activities] = await Promise.all([
        getCycles(),
        ifAllowed(can("classes:read"), getClasses, []),
        ifAllowed(can("enrollments:read"), getEnrollments, []),
        ifAllowed(can("teachers:read"), getTeachers, []),
        ifAllowed(can("staff:read"), getStaff, []),
        getActivities(),
      ]);
      return { cycles, classes, enrollments, teachers, staff, activities };
    },
    [],
  );

  // Chiffres financiers de la période, calculés par le serveur.
  const finance = useAsync(
    async () =>
      canReports
        ? Promise.all([getOverview(period), getByCycle(period)]).then(([overview, byCycle]) => ({ overview, byCycle }))
        : null,
    [canReports, period.from, period.to],
  );

  if (loading && !data) return <LoadingState label="Préparation du tableau de bord..." />;
  if (error || !data) {
    return (
      <div className="rounded-lg border border-accent-200 bg-accent-50/50 p-6 text-sm text-foreground-700">
        {error ?? "Erreur."}
        <Button size="sm" variant="outline" className="ml-3" icon="ri-refresh-line" onClick={reload}>
          Réessayer
        </Button>
      </div>
    );
  }

  const { cycles, classes, enrollments, teachers, staff, activities } = data;
  const primaireId = cycleIdOf(cycles, "PRIMAIRE");
  const secondaireId = cycleIdOf(cycles, "SECONDAIRE");
  const current = enrollments.filter((e) => e.schoolYear === schoolYear && isInClass(e.status));
  const primaire = current.filter((e) => e.cycleId === primaireId).length;
  const secondaire = current.filter((e) => e.cycleId === secondaireId).length;
  /** Étendue réelle d'un cycle, de la classe de plus petit niveau à la plus grande (« CP1 → CM2 »). */
  const classRange = (cycleId: string | undefined) => {
    const list = classes
      .filter((cls) => cls.cycleId === cycleId)
      .sort((a, b) => (a.level ?? 99) - (b.level ?? 99) || a.name.localeCompare(b.name));
    if (!list.length) return "Aucune classe";
    return list.length === 1 ? list[0].name : `${list[0].name} → ${list[list.length - 1].name}`;
  };

  const cycleLabels: Record<string, string> = {};
  cycles.forEach((c) => {
    cycleLabels[c.id] = c.label;
  });
  const figures = finance.data;
  const balances = (figures?.byCycle.cycles ?? []).map((c) => ({
    cycleId: c.cycleId,
    recettes: c.finance.totalRecettes,
    depenses: c.finance.totalDepenses,
    solde: c.finance.resultat,
  }));

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Vue d'ensemble"
        subtitle={`Année scolaire ${schoolYear}`}
        icon="ri-dashboard-3-line"
        actions={
          <>
            {can("enrollments:enroll") && (
              <Link to="/dashboard/enrollments">
                <Button variant="outline" icon="ri-file-add-line">
                  Nouvelle inscription
                </Button>
              </Link>
            )}
            {(can("revenues:read") || can("expenses:read")) && (
              <Link to="/dashboard/finance">
                <Button icon="ri-wallet-3-line">Gestion financière</Button>
              </Link>
            )}
          </>
        }
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Élèves inscrits" value={String(current.length)} icon="ri-user-3-line" tone="primary" hint={`${cycles.length} cycles actifs`} />
        <StatCard label="Cycle Primaire" value={String(primaire)} icon="ri-seedling-line" tone="warning" hint={classRange(primaireId)} />
        <StatCard label="Cycle Secondaire" value={String(secondaire)} icon="ri-book-read-line" tone="secondary" hint={classRange(secondaireId)} />
        <StatCard label="Personnel" value={String(teachers.length + staff.length)} icon="ri-team-line" tone="primary" hint={`${teachers.length} enseignants • ${staff.length} personnel`} />
      </div>

      {canReports && (
        <>
          <Card className="mt-5">
            <DateRangeBar value={period} onChange={setPeriod} />
          </Card>
          {finance.error ? (
            <p className="mt-4 text-sm text-accent-700">{finance.error}</p>
          ) : !figures ? (
            <LoadingState label="Calcul des finances de la période..." />
          ) : (
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
              <StatCard label="Recettes de la période" value={formatMoney(figures.overview.finance.totalRecettes)} icon="ri-arrow-up-circle-line" tone="primary" />
              <StatCard label="Dépenses de la période" value={formatMoney(figures.overview.finance.totalDepenses)} icon="ri-arrow-down-circle-line" tone="accent" />
              <StatCard
                label="Solde de la période"
                value={formatMoney(figures.overview.finance.resultat)}
                icon="ri-scales-3-line"
                tone="secondary"
                hint={`Reste à recouvrer (${figures.overview.school.schoolYear ?? schoolYear}) : ${formatMoney(figures.overview.fees.outstanding)}`}
              />
            </div>
          )}
        </>
      )}

      <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-5">
          {canReports && figures && <FinanceOverview balances={balances} cycleLabels={cycleLabels} />}
          <ClassDistribution cycles={cycles} classes={classes} enrollments={enrollments} schoolYear={schoolYear} />
        </div>
        <div className="space-y-5">
          <ActivityFeed activities={activities} />
          {canReports && figures && (
            <Card>
              <SectionTitle icon="ri-flag-2-line" title="Bilan par cycle" subtitle="Résultat de la période" />
              <div className="space-y-3">
                {balances.map((b) => (
                  <div key={b.cycleId} className="rounded-md border border-background-200 bg-background-100/50 p-3.5">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-foreground-900">{cycleLabels[b.cycleId]}</span>
                      <Badge tone={b.solde >= 0 ? "success" : "danger"} icon={b.solde >= 0 ? "ri-arrow-up-line" : "ri-arrow-down-line"}>
                        {b.solde >= 0 ? "Excédent" : "Déficit"}
                      </Badge>
                    </div>
                    <p className={`mt-1.5 text-lg font-semibold font-heading ${b.solde >= 0 ? "text-primary-700" : "text-accent-700"}`}>
                      {formatMoney(b.solde)}
                    </p>
                    <p className="text-xs text-foreground-500">
                      {formatMoney(b.recettes)} de recettes • {formatMoney(b.depenses)} de dépenses
                    </p>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
